/**
 * Notify Customers Modal — bulk SMS / email blast to every customer
 * scheduled in a date range. Shared component used by both Work Planner
 * (kebab menu / Actions sheet) and Customers tab.
 *
 * Built for emergencies (van written off, snow day, owner ill) — one
 * message goes out fast to everyone scheduled on the chosen day(s).
 */
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export const NotifyCustomersModal = ({ defaultDate, onClose, apiUrl, token }) => {
  const dateStr = defaultDate
    ? (defaultDate.toISOString ? defaultDate.toISOString().slice(0, 10) : String(defaultDate).slice(0, 10))
    : new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(dateStr);
  const [endDate, setEndDate] = useState(dateStr);
  const [channel, setChannel] = useState('sms');
  const [message, setMessage] = useState(
    "Hi [first_name], unfortunately we won't be able to clean your windows on [date] — our van is off the road. We'll be in touch shortly to reschedule. Sorry for the inconvenience. — [company_name]"
  );
  const [subject, setSubject] = useState('Update from [company_name]');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!startDate) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${apiUrl}/api/jobs/notify-customers/preview`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start_date: startDate,
        end_date: endDate || startDate,
        channel,
        message: 'preview',
      }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) setPreview(d); })
      .catch(() => { if (!cancelled) setPreview({ total: 0, recipients: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate, channel, apiUrl, token]);

  const insertToken = (tok) => setMessage(m => `${m}${tok}`);

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    const reachableSms = preview?.reachable_sms || 0;
    const reachableEmail = preview?.reachable_email || 0;
    const sendCount = channel === 'sms' ? reachableSms
      : channel === 'email' ? reachableEmail
      : (preview?.total || 0);
    if (sendCount === 0) { alert('No reachable customers on those dates.'); return; }
    const confirmText = channel === 'both'
      ? `Send to ${preview?.total || 0} customers via SMS + Email?`
      : `Send to ${sendCount} customer${sendCount === 1 ? '' : 's'} via ${channel === 'sms' ? 'SMS' : 'Email'}?`;
    if (!window.confirm(confirmText)) return;
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${apiUrl}/api/jobs/notify-customers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate || startDate,
          channel,
          message,
          email_subject: subject,
        }),
      });
      const data = await r.json();
      setResult(data);
    } catch (e) {
      setResult({ error: String(e) });
    } finally {
      setSending(false);
    }
  };

  const charCount = message.length;
  const smsSegments = charCount > 0 ? Math.ceil(charCount / 160) : 0;
  const channelLabel = channel === 'sms' ? 'SMS' : channel === 'email' ? 'Email' : 'SMS + Email';

  if (result && !result.error) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="notify-result-modal">
        <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <h2 className="text-2xl font-bold mb-1">Sent</h2>
            <p className="text-emerald-50">{result.message}</p>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex justify-between text-sm"><span className="text-zinc-500">Total customers</span><span className="font-semibold text-zinc-900">{result.total}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-500">SMS sent</span><span className="font-semibold text-blue-600">{result.sent_sms}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-500">Emails sent</span><span className="font-semibold text-amber-600">{result.sent_email}</span></div>
            {result.failed?.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm">
                <p className="font-semibold text-red-700 mb-2">{result.failed.length} failed:</p>
                <ul className="space-y-1 text-xs text-red-800 max-h-32 overflow-y-auto">
                  {result.failed.slice(0, 20).map((f, i) => (<li key={i}>{f.name} — {f.channel}: {f.reason}</li>))}
                </ul>
              </div>
            )}
            <button onClick={onClose} data-testid="notify-result-close" className="w-full mt-4 py-3 bg-zinc-900 text-white rounded-2xl font-semibold hover:bg-zinc-800">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="notify-customers-modal">
      <div className="bg-white w-full sm:max-w-lg modal-shell-h rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-6 bg-gradient-to-br from-indigo-600 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Notify Customers</h2>
              <p className="text-indigo-100 text-sm mt-1">
                {loading ? 'Loading…' : preview ? (
                  channel === 'sms' ? `${preview.reachable_sms} SMS-reachable`
                  : channel === 'email' ? `${preview.reachable_email} Email-reachable`
                  : `${preview.total} customer${preview.total === 1 ? '' : 's'} · SMS + Email`
                ) : ''}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="notify-close-btn"><X size={22} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="notify-start-date" className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="notify-end-date" className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Notify using</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="notify-channel-select" className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm bg-white">
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="both">SMS and Email</option>
            </select>
          </div>
          {channel !== 'sms' && (
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Email subject</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="notify-subject" className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm" />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Insert token</label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {['[first_name]', '[name]', '[service]', '[date]', '[address]', '[company_name]'].map(t => (
                <button key={t} type="button" onClick={() => insertToken(t)} data-testid={`notify-token-${t.replace(/[\[\]]/g, '')}`} className="px-2.5 py-1 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors">{t}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex justify-between items-baseline">
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Message</label>
              <span className="text-xs text-zinc-400">
                {charCount} char{charCount === 1 ? '' : 's'}
                {channel !== 'email' && smsSegments > 1 && ` · ${smsSegments} segments`}
              </span>
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} data-testid="notify-message-body" className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm font-mono" />
          </div>
          {preview?.recipients?.length > 0 && (
            <details className="bg-zinc-50 rounded-xl">
              <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-zinc-700">Recipients ({preview.recipients.length})</summary>
              <div className="px-3 pb-3 max-h-48 overflow-y-auto">
                <ul className="text-xs text-zinc-600 space-y-1">
                  {preview.recipients.map(r => (
                    <li key={r.id} className="flex justify-between items-center py-1 border-b border-zinc-100 last:border-0">
                      <span className="truncate">{r.name}</span>
                      <span className="ml-2 flex gap-1 shrink-0">
                        {r.has_phone && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">SMS</span>}
                        {r.has_email && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">Email</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          )}
        </div>
        <div className="p-4 border-t border-zinc-200 flex gap-3">
          <button onClick={onClose} data-testid="notify-cancel-btn" className="flex-1 py-3 bg-zinc-100 text-zinc-700 rounded-2xl font-semibold hover:bg-zinc-200">Cancel</button>
          <button onClick={handleSend} disabled={sending || !preview || preview.total === 0 || !message.trim()} data-testid="notify-send-btn" className="flex-[2] py-3 bg-gradient-to-br from-indigo-600 to-blue-600 text-white rounded-2xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:from-indigo-700 hover:to-blue-700">
            {sending ? 'Sending…' : `Send ${channelLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotifyCustomersModal;
